import { db, storage } from '../firebase/config';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  addDoc,
  serverTimestamp,
  onSnapshot,
  query,
  where,
  orderBy,
  limit
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import notificationService from './notificationService';

const EVENTS = 'events';
const REG = 'event_registrations';
const ATT = 'event_attendance';
const FB = 'event_feedback';
const CERT = 'event_certificates';
const QUIZ = 'event_quiz_attempts';
const EXP = 'event_expenses';
const APPR = 'approvals';
const REPTS = 'reports';

export async function createEvent(adminId, data) {
  const timeline = Array.isArray(data.timeline) ? data.timeline : [];
  const payload = {
    type: data.type,
    title: data.title,
    description: data.description || '',
    date: data.date || null,
    venue: data.venue || '',
    speakers: data.speakers || [],
    credit_cap: data.credit_cap || 0,
    cutoff: data.cutoff || 0,
    quiz: data.quiz || null,
    timeline,
    created_by: adminId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
  const ref = await addDoc(collection(db, EVENTS), payload);
  return ref.id;
}

export async function updateEvent(adminId, eventId, updates) {
  await updateDoc(doc(db, EVENTS, eventId), {
    ...updates,
    updatedAt: serverTimestamp()
  });
}

export function listenEvent(eventId, cb) {
  const unsub = onSnapshot(doc(db, EVENTS, eventId), (snap) => {
    if (!snap.exists()) return cb(null);
    cb({ id: snap.id, ...snap.data() });
  });
  return unsub;
}

export function listenEvents(cb, { type } = {}) {
  let q = collection(db, EVENTS);
  if (type) {
    q = query(q, where('type', '==', type), orderBy('createdAt', 'desc'));
  }
  const unsub = onSnapshot(q, (snap) => {
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    cb(list);
  });
  return unsub;
}

export async function getEvent(eventId) {
  const snap = await getDoc(doc(db, EVENTS, eventId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function setTimelineStageStatus(eventId, stageKey, status, data = {}) {
  const evt = await getEvent(eventId);
  if (!evt) throw new Error('Event not found');
  const timeline = Array.isArray(evt.timeline) ? evt.timeline.slice() : [];
  const idx = timeline.findIndex(s => s.key === stageKey);
  if (idx >= 0) {
    timeline[idx] = { ...timeline[idx], status, data: { ...(timeline[idx].data || {}), ...data }, updatedAt: new Date() };
  } else {
    timeline.push({ key: stageKey, name: stageKey, status, data, createdAt: new Date() });
  }
  await updateDoc(doc(db, EVENTS, eventId), { timeline, updatedAt: serverTimestamp() });
  try {
    await notificationService.createNotification({
      userId: evt.created_by,
      type: 'event_timeline',
      title: `Stage ${stageKey} updated`,
      message: `Status set to ${status}`,
      data: { eventId, stageKey, status }
    });
  } catch (_) { }
}

export async function registerForEvent(userId, eventId) {
  const evt = await getEvent(eventId);
  if (!evt) throw new Error('Event not found');
  const userSnap = await getDoc(doc(db, 'users', userId));
  if (!userSnap.exists()) throw new Error('User not found');
  const user = userSnap.data();
  if (evt.type === 'internal') {
    if ((user.totalCredits || 0) < (evt.credit_cap || 0)) {
      throw new Error('Not eligible: insufficient credits');
    }
  }
  if (evt.type === 'external') {
    const attemptId = `${eventId}_${userId}`;
    const attemptSnap = await getDoc(doc(db, QUIZ, attemptId));
    if (!attemptSnap.exists() || !attemptSnap.data().passed) {
      throw new Error('Not eligible: quiz not cleared');
    }
  }
  const regId = `${eventId}_${userId}`;
  await setDoc(doc(db, REG, regId), {
    eventId,
    userId,
    status: 'registered',
    createdAt: serverTimestamp()
  });
}

export async function registerForEventWithTeam(userId, eventId, members = []) {
  const evt = await getEvent(eventId);
  if (!evt) throw new Error('Event not found');
  if (evt.type === 'external') {
    const attemptId = `${eventId}_${userId}`;
    const attemptSnap = await getDoc(doc(db, QUIZ, attemptId));
    const passed = attemptSnap.exists() ? !!attemptSnap.data().passed : false;
    const score = attemptSnap.exists() ? Number(attemptSnap.data().score || 0) : 0;
    const cutoff = Number(evt.cutoff || 0);
    if (!attemptSnap.exists() || (!passed && score < cutoff)) {
      throw new Error('Not eligible: quiz not cleared');
    }
  } else {
    const userSnap = await getDoc(doc(db, 'users', userId));
    const user = userSnap.exists() ? userSnap.data() : null;
    if (!user) throw new Error('User not found');
    if ((user.totalCredits || 0) < (evt.credit_cap || 0)) {
      throw new Error('Not eligible: insufficient credits');
    }
  }
  if (!Array.isArray(members) || members.length < 1 || members.length > 5) {
    throw new Error('Team must have 1 to 5 members');
  }
  // Leader details
  const leaderSnap = await getDoc(doc(db, 'users', userId));
  const leader = leaderSnap.exists() ? leaderSnap.data() : {};
  const leaderName =
    (leader.firstName && leader.lastName) ? `${leader.firstName} ${leader.lastName}` :
      leader.displayName || leader.name || leader.fullName || leader.firstName || leader.email || null;
  const payload = {
    eventId,
    userId,
    leaderId: userId,
    leaderEmail: leader.email || null,
    leaderName,
    status: 'registered',
    team: members.map(m => ({ name: m.name, phone: m.phone, email: m.email })),
    updatedAt: serverTimestamp()
  };
  // Always create a new registration snapshot to avoid update-permission mismatch
  await addDoc(collection(db, REG), {
    ...payload,
    createdAt: serverTimestamp()
  });
}

export async function adminUpdateRegistrationTeam(registrationId, members = []) {
  await updateDoc(doc(db, REG, registrationId), {
    team: members.map(m => ({ name: m.name, phone: m.phone, email: m.email })),
    updatedAt: serverTimestamp()
  });
}

export async function ensureUserRegistration(eventId, userId) {
  const existing = await getUserRegistration(eventId, userId);
  if (existing?.id) return existing;
  const regRef = await addDoc(collection(db, REG), {
    eventId,
    userId,
    status: "registered",
    team: [],
    workflow: {},
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  const regSnap = await getDoc(regRef);
  return { id: regRef.id, ...(regSnap.data() || {}) };
}

export async function updateRegistrationWorkflowByUser(eventId, userId, workflowPatch = {}) {
  const reg = await ensureUserRegistration(eventId, userId);
  const currentWorkflow = reg.workflow || {};
  await updateDoc(doc(db, REG, reg.id), {
    workflow: { ...currentWorkflow, ...workflowPatch },
    updatedAt: serverTimestamp()
  });
}

export async function updateRegistrationWorkflowByAdmin(registrationId, workflowPatch = {}) {
  const regRef = doc(db, REG, registrationId);
  const regSnap = await getDoc(regRef);
  if (!regSnap.exists()) throw new Error("Registration not found");
  const reg = regSnap.data();
  const currentWorkflow = reg.workflow || {};
  await updateDoc(regRef, {
    workflow: { ...currentWorkflow, ...workflowPatch },
    updatedAt: serverTimestamp()
  });
}

export async function getUserExpenses(eventId, userId) {
  const snap = await getDocs(query(collection(db, EXP), where("eventId", "==", eventId), where("userId", "==", userId)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getEventExpenses(eventId) {
  const snap = await getDocs(query(collection(db, EXP), where("eventId", "==", eventId)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getEventAttendance(eventId) {
  const snap = await getDocs(query(collection(db, ATT), where("eventId", "==", eventId)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getEventFeedback(eventId) {
  const snap = await getDocs(query(collection(db, FB), where("eventId", "==", eventId)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getEventCertificates(eventId) {
  const snap = await getDocs(query(collection(db, CERT), where("eventId", "==", eventId)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function bulkUpsertCertificates(eventId, rows = []) {
  for (const row of rows) {
    const userId = String(row.userId || "").trim();
    const url = String(row.url || "").trim();
    if (!userId || !url) continue;
    await setDoc(doc(db, CERT, `${eventId}_${userId}`), {
      eventId,
      userId,
      url,
      updatedAt: serverTimestamp()
    }, { merge: true });
    const regs = await getDocs(query(collection(db, REG), where("eventId", "==", eventId), where("userId", "==", userId)));
    for (const regDoc of regs.docs) {
      await updateDoc(doc(db, REG, regDoc.id), {
        certificateUrl: url,
        updatedAt: serverTimestamp()
      });
    }
  }
}

export async function saveQuizAttempt(userId, eventId, score, passed) {
  const attemptId = `${eventId}_${userId}`;
  await setDoc(doc(db, QUIZ, attemptId), {
    eventId,
    userId,
    score,
    passed,
    createdAt: serverTimestamp()
  });
}

export async function markAttendanceAdmin(eventId, userId, attended, meta = {}) {
  const attId = `${eventId}_${userId}`;
  await setDoc(doc(db, ATT, attId), {
    eventId,
    userId,
    attended: !!attended,
    ...meta,
    updatedAt: serverTimestamp()
  }, { merge: true });
}

export async function submitFeedback(userId, eventId, payload) {
  const fbId = `${eventId}_${userId}`;
  await setDoc(doc(db, FB, fbId), {
    eventId,
    userId,
    ...payload,
    createdAt: serverTimestamp()
  });
}

export async function uploadCertificatesFile(eventId, file) {
  const path = `events/${eventId}/certificates/${file.name}`;
  const r = ref(storage, path);
  await uploadBytes(r, file);
  const url = await getDownloadURL(r);
  await setDoc(doc(db, CERT, `${eventId}_bundle`), {
    eventId,
    bundleUrl: url,
    uploadedAt: serverTimestamp()
  });
  return url;
}

export async function addExpense(userId, eventId, expense, file) {
  let billUrl = null;
  if (file) {
    const path = `events/${eventId}/expenses/${userId}/${Date.now()}_${file.name}`;
    const r = ref(storage, path);
    await uploadBytes(r, file);
    billUrl = await getDownloadURL(r);
  }
  await addDoc(collection(db, EXP), {
    eventId,
    userId,
    title: expense.title,
    amount: expense.amount,
    vendor: expense.vendor || '',
    billUrl: billUrl || null,
    createdAt: serverTimestamp(),
    status: 'submitted'
  });
}

export async function setApproval(eventId, userId, approval) {
  const id = `${eventId}_${userId}`;
  await setDoc(doc(db, APPR, id), {
    eventId,
    userId,
    approvedAmount: approval.approvedAmount || 0,
    authorityName: approval.authorityName || '',
    status: approval.status || 'approved',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  }, { merge: true });
}

export async function generateReport(eventId) {
  const regs = await getDocs(query(collection(db, REG), where('eventId', '==', eventId)));
  const fb = await getDocs(query(collection(db, FB), where('eventId', '==', eventId)));
  const exps = await getDocs(query(collection(db, EXP), where('eventId', '==', eventId)));
  const appr = await getDocs(query(collection(db, APPR), where('eventId', '==', eventId)));
  const data = {
    eventId,
    participants: regs.docs.map(d => d.data()),
    feedback: fb.docs.map(d => d.data()),
    expenses: exps.docs.map(d => d.data()),
    approvals: appr.docs.map(d => d.data()),
    generatedAt: new Date().toISOString()
  };
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const path = `events/${eventId}/reports/${Date.now()}_report.json`;
  const r = ref(storage, path);
  await uploadBytes(r, blob);
  const url = await getDownloadURL(r);
  await addDoc(collection(db, REPTS), {
    eventId,
    url,
    createdAt: serverTimestamp()
  });
  return url;
}

export async function setEventQuiz(eventId, quiz) {
  await updateDoc(doc(db, EVENTS, eventId), {
    quiz,
    updatedAt: serverTimestamp()
  });
}

export async function getRegistrations(eventId) {
  const snap = await getDocs(query(collection(db, REG), where('eventId', '==', eventId)));
  const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  // Keep latest registration per user to avoid duplicates across re-submissions
  const latestByUser = new Map();
  for (const item of list) {
    const key = item.userId || item.leaderId || item.id;
    const prev = latestByUser.get(key);
    const currTime = item.updatedAt?.seconds || item.createdAt?.seconds || 0;
    const prevTime = prev?.updatedAt?.seconds || prev?.createdAt?.seconds || 0;
    if (!prev || currTime >= prevTime) latestByUser.set(key, item);
  }
  return Array.from(latestByUser.values());
}

export async function getUserRegistration(eventId, userId) {
  const snap = await getDocs(query(collection(db, REG), where('eventId', '==', eventId), where('userId', '==', userId)));
  if (snap.empty) return null;
  const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  list.sort((a, b) => {
    const at = a.updatedAt?.seconds || a.createdAt?.seconds || 0;
    const bt = b.updatedAt?.seconds || b.createdAt?.seconds || 0;
    return bt - at;
  });
  return list[0];
}

export async function setUserCertificate(eventId, userId, url) {
  const id = `${eventId}_${userId}`;
  await setDoc(doc(db, CERT, id), {
    eventId,
    userId,
    url,
    createdAt: serverTimestamp()
  }, { merge: true });
}

export async function createTeam(eventId, name) {
  await addDoc(collection(db, 'event_teams'), {
    eventId,
    name,
    members: [],
    createdAt: serverTimestamp()
  });
}

export async function getTeams(eventId) {
  const snap = await getDocs(query(collection(db, 'event_teams'), where('eventId', '==', eventId)));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function addTeamMember(eventId, teamId, emailOrUid) {
  const ref = doc(db, 'event_teams', teamId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Team not found');
  const data = snap.data();
  const members = Array.isArray(data.members) ? data.members.slice() : [];
  if (!members.includes(emailOrUid)) members.push(emailOrUid);
  await updateDoc(ref, { members, updatedAt: serverTimestamp() });
}

export default {
  createEvent,
  updateEvent,
  listenEvent,
  listenEvents,
  getEvent,
  setTimelineStageStatus,
  registerForEvent,
  registerForEventWithTeam,
  saveQuizAttempt,
  markAttendanceAdmin,
  submitFeedback,
  uploadCertificatesFile,
  addExpense,
  setApproval,
  generateReport,
  setEventQuiz,
  getRegistrations,
  getUserRegistration,
  setUserCertificate,
  adminUpdateRegistrationTeam,
  ensureUserRegistration,
  updateRegistrationWorkflowByUser,
  updateRegistrationWorkflowByAdmin,
  getUserExpenses,
  getEventExpenses,
  getEventAttendance,
  getEventFeedback,
  getEventCertificates,
  bulkUpsertCertificates,
  createTeam,
  getTeams,
  addTeamMember
};
